import { getDb, type SyncQueueEntry } from "./offlineDb";
import { supabase } from "@/integrations/supabase/client";

// ── Enqueue a mutation ──────────────────────────────────────────
export async function enqueue(entry: Omit<SyncQueueEntry, "id" | "created_at" | "retry_count" | "last_error" | "status">): Promise<void> {
  const db = await getDb();
  const id = crypto.randomUUID();
  await db.put("sync_queue", {
    ...entry,
    id,
    created_at: new Date().toISOString(),
    retry_count: 0,
    last_error: null,
    status: "queued",
  });
}

// ── Get pending count ───────────────────────────────────────────
export async function getPendingCount(): Promise<number> {
  const db = await getDb();
  const all = await db.getAllFromIndex("sync_queue", "by-status", "queued");
  const failed = await db.getAllFromIndex("sync_queue", "by-status", "failed");
  // failed items with retry_count < 5 are still retryable
  return all.length + failed.filter((f) => f.retry_count < 5).length;
}

// ── Flush queue ─────────────────────────────────────────────────
const TABLE_MAP: Record<string, string> = {
  workout_templates: "workout_templates",
  template_exercises: "template_exercises",
  workout_sessions: "workout_sessions",
  session_exercises: "session_exercises",
  session_sets: "session_sets",
  food_logs: "food_logs",
  body_metrics: "body_metrics",
  check_ins: "check_ins",
  custom_foods: "custom_foods",
  macro_targets: "macro_targets",
  macro_calculations: "macro_calculations",
  orm_calculations: "orm_calculations",
  water_logs: "water_logs",
  meal_templates: "meal_templates",
  meal_template_items: "meal_template_items",
};

export async function flush(): Promise<{ synced: number; failed: number }> {
  const db = await getDb();
  const queued = await db.getAllFromIndex("sync_queue", "by-status", "queued");
  // Also retry failed items with retry_count < 5
  const retryable = (await db.getAllFromIndex("sync_queue", "by-status", "failed")).filter((e) => e.retry_count < 5);
  const items = [...queued, ...retryable].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  let synced = 0;
  let failed = 0;

  for (const item of items) {
    const table = TABLE_MAP[item.entity_type];
    if (!table) {
      // Unknown entity type — mark as failed permanently
      await db.put("sync_queue", { ...item, status: "failed", last_error: "Unknown entity type", retry_count: 99 });
      failed++;
      continue;
    }

    // Mark as syncing
    await db.put("sync_queue", { ...item, status: "syncing" });

    try {
      let error: any = null;
      if (item.operation === "create") {
        const { _syncStatus, ...payload } = item.payload_json;
        const result = await supabase.from(table as any).insert(payload);
        error = result.error;
      } else if (item.operation === "update") {
        const { _syncStatus, id, ...payload } = item.payload_json;
        const result = await supabase.from(table as any).update(payload).eq("id", item.entity_id);
        error = result.error;
      } else if (item.operation === "delete") {
        const result = await supabase.from(table as any).delete().eq("id", item.entity_id);
        error = result.error;
      }

      if (error) throw error;

      // Success — remove from queue and mark local record as synced
      await db.delete("sync_queue", item.id);

      // Update the local record's sync status
      try {
        const storeName = item.entity_type as any;
        const localRecord = await db.get(storeName, item.entity_id);
        if (localRecord && item.operation !== "delete") {
          await db.put(storeName, { ...localRecord, _syncStatus: "synced" });
        }
      } catch {
        // Store might not exist for this entity type
      }

      synced++;
    } catch (err: any) {
      const retryCount = item.retry_count + 1;
      const newStatus = retryCount >= 5 ? "failed" : "queued";
      await db.put("sync_queue", {
        ...item,
        status: newStatus,
        retry_count: retryCount,
        last_error: err?.message || "Unknown error",
      });
      failed++;
    }
  }

  return { synced, failed };
}

// ── Clear synced entries ────────────────────────────────────────
export async function clearSynced(): Promise<void> {
  const db = await getDb();
  const synced = await db.getAllFromIndex("sync_queue", "by-status", "synced");
  const tx = db.transaction("sync_queue", "readwrite");
  for (const entry of synced) {
    await tx.store.delete(entry.id);
  }
  await tx.done;
}

// ── Clear entire queue ──────────────────────────────────────────
export async function clearQueue(): Promise<void> {
  const db = await getDb();
  await db.clear("sync_queue");
}
